import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Label, Col, Row } from 'reactstrap';
import { WRITE_CONTACTS } from 'services/enums/users/permissions';
import useHasPermission from 'services/users/useHasPermission';
import { useHistory, useParams } from 'react-router-dom';

import MultiSelectDropdown from 'components/layout/forms/MultiSelectDropdown';
import ModalConfirm from 'components/layout/modals/ModalConfirm';
import PageContainer from 'components/layout/PageContainer';
import { fetchOrganizationUnits } from 'store/users/actions';
import IOrganizationUnit from 'interfaces/users/IOrganizationUnit';
import ModalAccept from 'components/layout/modals/ModalAccept';
import TextInputField from 'components/layout/forms/TextInputField';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import customerApi, { IContactBranch } from 'api/contacts';
import IUserGet from 'api/interfaces/users/IUserGet';
import { makeEmail } from 'services/contacts/emailFormats';
import {
  formatPhone,
  getCountryCode,
  withCountryCode,
} from 'services/contacts/phoneNumbers';
import showOrganizationUnits from './showOrganizationUnits';
import translations from '../../services/translations/translations';

import '../../style/contacts/CreateContactPerson.css';
import ContactTitleField from './ContactTitleField';
import LinkedOffers from './LinkedOffers';

const CreateContactPerson = ({
  organizationUnitTypesList,
  fetchOrganizationUnitTypesList,
}: {
  organizationUnitTypesList: IOrganizationUnit[];
  fetchOrganizationUnitTypesList: () => void;
}): JSX.Element | null => {
  const { t } = useTranslation([
    translations.contacts,
    translations.common,
    translations.admin,
    translations.products,
  ]);
  const history = useHistory();
  const canWrite = useHasPermission(WRITE_CONTACTS);
  const { contactId, personId } = useParams();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [organizationUnitTypeOptions, setOrganizationUnitTypeOptions] =
    useState<{ label: string; value: number }[]>([]);
  const [organizationUnits, setOrganizationUnits] = useState<
    { label: string; value: number }[]
  >([]);
  const [person, setPerson] = useState({
    contact: { id: 0 },
    email: '',
    firstName: '',
    jobDescription: '',
    lastName: '',
    // The department or area of the company ('' when common to all) and its area
    branchId: '' as number | string,
    operatingArea: '',
    phone: '',
    phoneWithCountryCode: '',
    title: '',
  });
  const [modalSaved, setModalSaved] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const modalMessage = saveSuccess
    ? t(`${translations.contacts}:Contact saved`)
    : t(`${translations.contacts}:Contact failed`);

  // The e-mail form and ending of the company: the e-mail of a new person is made of them
  // and the names until the user types the e-mail (emptying it makes it again)
  const [companyEmail, setCompanyEmail] = useState<{
    ending: null | string;
    format: null | string;
  }>({ ending: null, format: null });
  const [emailTyped, setEmailTyped] = useState(false);
  // The country code of the phones: of the country of the company, else of the department
  // of the user; the number with it is made of the phone until the user types it
  const currentUser = useSelector(
    (state: { users: { current: IUserGet | null } }) => state.users.current
  );
  const departmentCountry = currentUser?.organizationUnit?.country ?? null;
  const [companyCountry, setCompanyCountry] = useState<null | string>(null);
  const countryCode = getCountryCode(companyCountry, departmentCountry);
  const [countryCodeTyped, setCountryCodeTyped] = useState(false);

  // The departments or areas of the company: the person is in one or common to all
  const [branches, setBranches] = useState<IContactBranch[]>([]);
  const setBranchesOf = (companyId: number | string | undefined) => {
    if (companyId === undefined || companyId === null) {
      return;
    }
    customerApi.branches
      .get(companyId)
      .then(setBranches)
      .catch(() => undefined);
  };
  // Toimialue: the areas of the departments of the company, and one saved before
  const operatingAreas = Array.from(
    new Set(
      branches
        .map((branch) => branch.operatingArea)
        .filter((area) => area !== '')
    )
  );

  const handleChange = (e: any) => {
    const { name } = e.target;
    // A mobile number is spaced as XXX XXX XXXX
    const value =
      name === 'phone' ? formatPhone(e.target.value) : e.target.value;
    if (name === 'email') {
      setEmailTyped(value !== '');
    }
    if (name === 'phoneWithCountryCode') {
      setCountryCodeTyped(value !== '');
    }
    setPerson((prevState) => {
      const changed = { ...prevState, [name]: value };
      if (name === 'phone' && !countryCodeTyped) {
        changed.phoneWithCountryCode = withCountryCode(value, countryCode);
      }
      const makesEmail =
        personId === undefined &&
        !emailTyped &&
        (name === 'firstName' || name === 'lastName');
      return makesEmail
        ? {
            ...changed,
            email: makeEmail(
              companyEmail.format,
              changed.firstName,
              changed.lastName,
              companyEmail.ending
            ),
          }
        : changed;
    });
  };

  const goPreviousPage = () => {
    if (personId !== undefined) {
      history.push(`/contacts/${person.contact.id}`);
      return;
    }

    history.push(`/contacts/${contactId}`);
  };

  useEffect(() => {
    fetchOrganizationUnitTypesList();
    const getContact = async () => {
      if (personId !== undefined) {
        try {
          const result: any = await customerApi.getPerson(personId);
          if (result !== undefined && result.removed) {
            goPreviousPage();
          } else if (result !== undefined) {
            // A number with the country code of its own is kept when the phone changes
            const code = getCountryCode(
              result.contact?.addressCountry,
              departmentCountry
            );
            setCompanyCountry(result.contact?.addressCountry ?? null);
            setCountryCodeTyped(
              Boolean(result.phoneWithCountryCode) &&
                result.phoneWithCountryCode !==
                  withCountryCode(result.phone ?? '', code)
            );
            setBranchesOf(result.contact?.id);
            // The persons saved before have no department or operating area
            setPerson({
              ...result,
              branchId: result.branchId ?? '',
              operatingArea: result.operatingArea ?? '',
            });
            setOrganizationUnits(
              showOrganizationUnits(result?.organizationUnits)
            );
            setOrganizationUnitTypeOptions(
              showOrganizationUnits(result?.contact.organizationUnits)
            );
          }
        } catch (error) {
          // define action
        }
      } else {
        try {
          const result: any = await customerApi.getContact(contactId);
          setCompanyEmail({
            ending: result?.emailEnding ?? null,
            format: result?.emailFormat ?? null,
          });
          setCompanyCountry(result?.address?.country ?? null);
          setBranchesOf(contactId);
          // A company without departments of its own: all of them to choose from
          if ((result?.organizationUnits ?? []).length === 0) {
            setOrganizationUnitTypeOptions(
              showOrganizationUnits(organizationUnitTypesList)
            );
          } else {
            setOrganizationUnitTypeOptions(
              showOrganizationUnits(result?.organizationUnits)
            );
          }
        } catch (error) {
          // define action
        }
      }
    };
    getContact().catch((e) => console.log(e));
  }, [personId]);

  const addUpdateContactPerson = async () => {
    const formattedPerson = {
      email: person.email,
      firstName: person.firstName,
      jobDescription: person.jobDescription,
      lastName: person.lastName,
      branchId: person.branchId === '' ? null : Number(person.branchId),
      operatingArea: person.operatingArea,
      phone: person.phone,
      phoneWithCountryCode: person.phoneWithCountryCode,
      title: person.title,
      organizationUnitIds: organizationUnits.map((org) => org.value),
    };
    let result;
    if (personId === undefined) {
      try {
        result = await customerApi.postPerson(contactId, formattedPerson);
        setSaveSuccess(true);
        setModalSaved(true);
        history.push(`/contacts/persons/${result.id}`);
      } catch (error) {
        setSaveSuccess(false);
        setModalSaved(true);
      }
    } else {
      try {
        result = await customerApi.putPerson(personId, formattedPerson);
        setSaveSuccess(true);
        setModalSaved(true);
      } catch (error) {
        setSaveSuccess(false);
        setModalSaved(true);
      }
    }
  };

  const deleteContactPerson = () => {
    customerApi.deletePerson(personId).then(() => {
      goPreviousPage();
    });
  };

  const handleOrganizationUnits = (e: any) => {
    setOrganizationUnits(e);
  };

  return (
    <PageContainer>
      <h2 className="header-text">{t('Contact Person Info')}</h2>
      <div className="content-background">
        <Row>
          <Col>
            <TextInputField
              label={t('First Name')}
              name="firstName"
              onChange={handleChange}
              value={person.firstName}
            />
          </Col>
          <Col>
            <TextInputField
              label={t('Last Name')}
              name="lastName"
              onChange={handleChange}
              value={person.lastName}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <TextInputField
              label={t('Email')}
              name="email"
              onChange={handleChange}
              value={person.email}
            />
          </Col>
          <Col>
            <TextInputField
              label={t('Phone')}
              name="phone"
              onChange={handleChange}
              value={person.phone}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <TextInputField
              label={t('Job Description')}
              name="jobDescription"
              onChange={handleChange}
              value={person.jobDescription}
            />
          </Col>
          <Col>
            <TextInputField
              label={t('Phone With Country Code')}
              name="phoneWithCountryCode"
              onChange={handleChange}
              value={person.phoneWithCountryCode}
            />
          </Col>
        </Row>
        {/* Toimipiste: a department or area of the company or all of them; toimialue of
            the areas of the departments, set by the department chosen */}
        <Row>
          <Col>
            <SelectInputFieldControlled
              id="branchId"
              label={t('Office')}
              name="branchId"
              onChange={(event: any) => {
                // Read before the event is reused (React 16)
                const { value } = event.target;
                const branch = branches.find(
                  (candidate) => String(candidate.id) === value
                );
                setPerson((prevState) => ({
                  ...prevState,
                  branchId: value,
                  operatingArea:
                    branch?.operatingArea || prevState.operatingArea,
                }));
              }}
              options={[
                <option key="" value="">
                  {t('All areas')}
                </option>,
                ...branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                )),
              ]}
              value={String(person.branchId ?? '')}
            />
          </Col>
          <Col>
            <SelectInputFieldControlled
              id="operatingArea"
              label={t('Operating area')}
              name="operatingArea"
              onChange={handleChange}
              options={[
                <option key="" value="">
                  -
                </option>,
                ...operatingAreas
                  .concat(
                    person.operatingArea &&
                      !operatingAreas.includes(person.operatingArea)
                      ? [person.operatingArea]
                      : []
                  )
                  .map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  )),
              ]}
              value={person.operatingArea ?? ''}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <ContactTitleField
              label={t('Title')}
              onChange={handleChange}
              value={person.title}
            />
          </Col>
          <Col>
            <Label className="label">
              {t(`${translations.admin}:Organization Units`)}
            </Label>
            <MultiSelectDropdown
              placeholder={t(`${translations.products}:Select`)}
              required
              name="organizationUnits"
              value={organizationUnits}
              onChange={handleOrganizationUnits}
              options={organizationUnitTypeOptions}
            />
          </Col>
        </Row>
        <span>
          <Button
            className="add-button-margin contact-button"
            onClick={goPreviousPage}
          >
            {t('common:Back')}
          </Button>
          {/* With only the right to read, the person is not saved or deleted */}
          {canWrite ? (
            <Button
              className="add-button-margin contact-button float-right"
              onClick={addUpdateContactPerson}
            >
              {t('common:Save')}
            </Button>
          ) : null}
          {personId !== undefined && canWrite ? (
            <>
              <Button
                className="add-button-margin contact-button"
                onClick={() => setDeleteConfirm(true)}
              >
                {t('Delete Person')}
              </Button>
              <ModalConfirm
                showing={deleteConfirm}
                setShowing={setDeleteConfirm}
                header={t('common:Confirm Delete')}
                text={t('common:Confirm Delete Text', {
                  target: `${person.firstName} ${person.lastName}`,
                })}
                yesButton={t('common:Confirm')}
                noButton={t('common:Cancel')}
                confirm={deleteContactPerson}
              />
              <ModalAccept
                showing={modalSaved}
                setShowing={setModalSaved}
                header=""
                text={modalMessage}
              />
            </>
          ) : null}
        </span>
      </div>
      {personId !== undefined ? (
        <div className="content-background">
          <LinkedOffers contactPersonId={personId} />
        </div>
      ) : null}
    </PageContainer>
  );
};

const mapStateToProps = (state: any) => ({
  organizationUnitTypesList: state.users.organizationUnits,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchOrganizationUnitTypesList: () => dispatch(fetchOrganizationUnits()),
});
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateContactPerson);
